import { WorkPage, WorkSection, WorkEmpty } from '../components/work-page.tsx';
import { TableScroll } from '@web/components/table-scroll.tsx';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { logsApi } from '../api/endpoints.ts';
import { Alert, LoadingBlock, Pagination, Select, classes } from '../components/ui.tsx';
import { useDocumentTitle } from '../hooks/index.ts';
import { formatCompanyInstant } from '../i18n/datetime.ts';
import { EMAIL_LOG_STATUSES, SMS_LOG_STATUSES } from '@shared/constants/index.ts';

/**
 * Every log the company keeps, in one owner-only place: the audit trail (who
 * did what, when), the email ledger and the SMS ledger. Each tab is its own
 * paged list with its own filter — the tabs share nothing but the page shell,
 * because "what happened" and "did it arrive" are different questions over
 * different tables.
 *
 * The activity tab used to be its own /activity page, visible to managers via
 * reports:read. It lives here now, behind logs:read, which only owners hold —
 * the ledgers beside it list every customer's email address and phone number.
 */

const TABS = ['activity', 'email', 'sms'] as const;
type LogTab = (typeof TABS)[number];

/** The entity types worth a filter of their own; everything else is "all". */
const FILTERABLE_TYPES = [
  'quote',
  'customer',
  'appointment',
  'appointment_series',
  'company',
  'quote_request',
] as const;

/**
 * The label carries the meaning; color only reinforces it (spec §27). One
 * palette for both ledgers: an outcome reads the same whether it traveled as
 * an email or a text.
 */
const OUTCOME_TONE: Record<string, string> = {
  queued: 'border-[color:var(--color-line)] text-[color:var(--color-muted)]',
  sent: 'border-[color:var(--color-brand)] text-[color:var(--color-brand)]',
  delivered:
    'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]',
  failed: 'border-[color:var(--color-danger)] text-[color:var(--color-danger)]',
  bounced: 'border-[color:var(--color-danger)] text-[color:var(--color-danger)]',
  undelivered: 'border-[color:var(--color-danger)] text-[color:var(--color-danger)]',
  suppressed: 'border-[color:var(--color-warning)] text-[color:var(--color-warning)]',
};

function OutcomeBadge({ status, kind }: { status: string; kind: 'email' | 'sms' }) {
  const { t } = useTranslation();
  return (
    <span className={classes('badge', OUTCOME_TONE[status] ?? OUTCOME_TONE.queued)}>
      {t(`logs.${kind}Status.${status}`, { defaultValue: status })}
    </span>
  );
}

function useLogDateFormat() {
  return (value: string) => formatCompanyInstant(new Date(value), { dateStyle: 'medium', timeStyle: 'short' });
}

export default function LogsPage() {
  const { t } = useTranslation();
  useDocumentTitle(t('logs.documentTitle'));

  const [tab, setTab] = useState<LogTab>('activity');

  return (
    <WorkPage title={t('logs.title')} description={t('logs.description')}>
      <div role="tablist" aria-label={t('logs.tabsAriaLabel')} className="mb-6 flex flex-wrap gap-1">
        {TABS.map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            id={`logs-tab-${value}`}
            aria-selected={tab === value}
            aria-controls={`logs-panel-${value}`}
            onClick={() => setTab(value)}
            className={classes(
              'rounded-md px-3 py-2 font-medium',
              tab === value
                ? 'bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]'
                : 'text-[color:var(--color-muted)] hover:bg-[color:var(--color-surface)]',
            )}
          >
            {t(`logs.tab.${value}`)}
          </button>
        ))}
      </div>

      <WorkSection title={t(`logs.tab.${tab}`)}>
        <div role="tabpanel" id={`logs-panel-${tab}`} aria-labelledby={`logs-tab-${tab}`}>
          {tab === 'activity' ? <ActivityTab /> : tab === 'email' ? <EmailTab /> : <SmsTab />}
        </div>
      </WorkSection>
    </WorkPage>
  );
}

/**
 * The company-wide audit trail as people read it: who did what, when, newest
 * first. The same rows have always backed each quote's history panel — this is
 * the whole company in one place.
 */
function ActivityTab() {
  const { t } = useTranslation();
  const [entityType, setEntityType] = useState('');
  const [page, setPage] = useState(1);

  const query = useQuery({
    queryKey: ['logs', 'activity', { entityType, page }],
    queryFn: () => logsApi.activity({ entityType: entityType || undefined, page, pageSize: 50 }),
  });

  const dateFormat = useLogDateFormat();

  const TYPE_OPTIONS = [
    { value: '', label: t('activity.typeAll') },
    ...FILTERABLE_TYPES.map((value) => ({ value, label: t(`activity.type.${value}`) })),
  ];

  return (
    <>
      <div className="mb-6 max-w-sm">
        <Select
          label={t('activity.typeLabel')}
          options={TYPE_OPTIONS}
          value={entityType}
          onChange={(event) => {
            setEntityType(event.target.value);
            setPage(1);
          }}
        />
      </div>

      {query.isLoading ? <LoadingBlock label={t('activity.loading')} /> : null}
      {query.isError ? (
        <Alert tone="error" title={t('activity.loadErrorTitle')}>
          {(query.error as Error).message}
        </Alert>
      ) : null}

      {query.data && query.data.items.length === 0 ? <WorkEmpty title={t('activity.emptyTitle')} /> : null}

      {query.data && query.data.items.length > 0 ? (
        <TableScroll
          aria-label={t('activity.caption', { count: query.data.total })}
          className="overflow-x-auto rounded-lg border border-[color:var(--color-line)] bg-white"
        >
          <table className="data-table w-full min-w-max border-collapse text-left text-sm">
            <caption className="sr-only">{t('activity.caption', { count: query.data.total })}</caption>
            <thead>
              <tr className="border-b border-[color:var(--color-line)] bg-[color:var(--color-surface)]">
                <th scope="col" className="px-4 py-3 font-semibold">
                  {t('activity.columnWhen')}
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  {t('activity.columnWho')}
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  {t('activity.columnWhat')}
                </th>
              </tr>
            </thead>
            <tbody>
              {query.data.items.map((event) => (
                <tr key={event.id} className="border-b border-[color:var(--color-line)] last:border-0">
                  <td className="whitespace-nowrap px-4 py-3 text-[color:var(--color-muted)]">
                    {dateFormat(event.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    {event.userName ?? t('activity.actorCustomer')}
                    {/* Whose account was used is not always who used it. A
                        support sign-in says so on the row itself, so this
                        page cannot quietly credit the customer with work
                        somebody else did in their name. */}
                    {event.impersonatedBy ? (
                      <span className="mt-0.5 block text-xs text-[color:var(--color-warning)]">
                        {t('activity.actorSupport', { admin: event.impersonatedBy })}
                      </span>
                    ) : null}
                    {/* And an integration is not the person either: the row
                        carries their authority, but naming the credential is
                        what makes a key that has gone wrong findable. */}
                    {event.viaApiKey ? (
                      <span className="mt-0.5 block text-xs text-[color:var(--color-muted)]">
                        {t('activity.actorApiKey', { name: event.viaApiKey.name })}
                      </span>
                    ) : null}
                  </td>
                  {/* A summary is a sentence, not a token: bounded so one
                      long event cannot set the width of the whole table. */}
                  <td className="table-text max-w-[24rem] px-4 py-3">
                    {event.entityType === 'quote' ? (
                      <Link
                        to={`/quotes/${event.entityId}`}
                        className="text-[color:var(--color-brand)] hover:underline"
                      >
                        {event.summary ?? event.action}
                      </Link>
                    ) : event.entityType === 'customer' ? (
                      <Link
                        to={`/customers/${event.entityId}`}
                        className="text-[color:var(--color-brand)] hover:underline"
                      >
                        {event.summary ?? event.action}
                      </Link>
                    ) : (
                      (event.summary ?? event.action)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      ) : null}

      <Pagination page={page} totalPages={query.data?.totalPages} onPageChange={setPage} />
    </>
  );
}

/**
 * The email ledger: one row per attempt, including the refusals that never
 * reached a provider. `failure_reason` rides under the outcome badge because
 * it is only ever read when the outcome went wrong.
 */
function EmailTab() {
  const { t } = useTranslation();
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);

  const query = useQuery({
    queryKey: ['logs', 'email', { status, page }],
    queryFn: () => logsApi.email({ status: status || undefined, page, pageSize: 50 }),
  });

  const dateFormat = useLogDateFormat();

  const STATUS_OPTIONS = [
    { value: '', label: t('logs.statusAll') },
    ...EMAIL_LOG_STATUSES.map((value) => ({ value, label: t(`logs.emailStatus.${value}`) })),
  ];

  return (
    <>
      <div className="mb-6 max-w-sm">
        <Select
          label={t('logs.statusLabel')}
          options={STATUS_OPTIONS}
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            setPage(1);
          }}
        />
      </div>

      {query.isLoading ? <LoadingBlock label={t('logs.email.loading')} /> : null}
      {query.isError ? (
        <Alert tone="error" title={t('logs.email.loadErrorTitle')}>
          {(query.error as Error).message}
        </Alert>
      ) : null}

      {query.data && query.data.items.length === 0 ? <WorkEmpty title={t('logs.email.emptyTitle')} /> : null}

      {query.data && query.data.items.length > 0 ? (
        <TableScroll
          aria-label={t('logs.email.caption', { count: query.data.total })}
          className="overflow-x-auto rounded-lg border border-[color:var(--color-line)] bg-white"
        >
          <table className="data-table w-full min-w-max border-collapse text-left text-sm">
            <caption className="sr-only">{t('logs.email.caption', { count: query.data.total })}</caption>
            <thead>
              <tr className="border-b border-[color:var(--color-line)] bg-[color:var(--color-surface)]">
                <th scope="col" className="px-4 py-3 font-semibold">
                  {t('logs.columnWhen')}
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  {t('logs.columnType')}
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  {t('logs.columnRecipient')}
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  {t('logs.columnOutcome')}
                </th>
              </tr>
            </thead>
            <tbody>
              {query.data.items.map((entry) => (
                <tr key={entry.id} className="border-b border-[color:var(--color-line)] last:border-0">
                  <td className="whitespace-nowrap px-4 py-3 text-[color:var(--color-muted)]">
                    {dateFormat(entry.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    {entry.quoteId ? (
                      <Link to={`/quotes/${entry.quoteId}`} className="text-[color:var(--color-brand)] hover:underline">
                        {t(`logs.emailType.${entry.emailType}`, { defaultValue: entry.emailType })}
                      </Link>
                    ) : (
                      t(`logs.emailType.${entry.emailType}`, { defaultValue: entry.emailType })
                    )}
                  </td>
                  <td className="table-text max-w-[16rem] px-4 py-3">{entry.recipient}</td>
                  <td className="px-4 py-3">
                    <OutcomeBadge status={entry.status} kind="email" />
                    {entry.failureReason ? (
                      <div className="table-text mt-1 max-w-[20rem] text-xs break-words text-[color:var(--color-muted)]">
                        {entry.failureReason}
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      ) : null}

      <Pagination page={page} totalPages={query.data?.totalPages} onPageChange={setPage} />
    </>
  );
}

/**
 * The SMS ledger. Message bodies are deliberately never stored, so the row is
 * the message's paper trail: what kind, to whom, how many billed segments, and
 * whether the carrier delivered it.
 */
function SmsTab() {
  const { t } = useTranslation();
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);

  const query = useQuery({
    queryKey: ['logs', 'sms', { status, page }],
    queryFn: () => logsApi.sms({ status: status || undefined, page, pageSize: 50 }),
  });

  const dateFormat = useLogDateFormat();

  const STATUS_OPTIONS = [
    { value: '', label: t('logs.statusAll') },
    ...SMS_LOG_STATUSES.map((value) => ({ value, label: t(`logs.smsStatus.${value}`) })),
  ];

  return (
    <>
      <div className="mb-6 max-w-sm">
        <Select
          label={t('logs.statusLabel')}
          options={STATUS_OPTIONS}
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            setPage(1);
          }}
        />
      </div>

      {query.isLoading ? <LoadingBlock label={t('logs.sms.loading')} /> : null}
      {query.isError ? (
        <Alert tone="error" title={t('logs.sms.loadErrorTitle')}>
          {(query.error as Error).message}
        </Alert>
      ) : null}

      {query.data && query.data.items.length === 0 ? <WorkEmpty title={t('logs.sms.emptyTitle')} /> : null}

      {query.data && query.data.items.length > 0 ? (
        <TableScroll
          aria-label={t('logs.sms.caption', { count: query.data.total })}
          className="overflow-x-auto rounded-lg border border-[color:var(--color-line)] bg-white"
        >
          <table className="data-table w-full min-w-max border-collapse text-left text-sm">
            <caption className="sr-only">{t('logs.sms.caption', { count: query.data.total })}</caption>
            <thead>
              <tr className="border-b border-[color:var(--color-line)] bg-[color:var(--color-surface)]">
                <th scope="col" className="px-4 py-3 font-semibold">
                  {t('logs.columnWhen')}
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  {t('logs.columnType')}
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  {t('logs.columnRecipient')}
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  {t('logs.sms.columnSegments')}
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  {t('logs.columnOutcome')}
                </th>
              </tr>
            </thead>
            <tbody>
              {query.data.items.map((entry) => (
                <tr key={entry.id} className="border-b border-[color:var(--color-line)] last:border-0">
                  <td className="whitespace-nowrap px-4 py-3 text-[color:var(--color-muted)]">
                    {dateFormat(entry.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    {entry.quoteId ? (
                      <Link to={`/quotes/${entry.quoteId}`} className="text-[color:var(--color-brand)] hover:underline">
                        {t(`logs.smsType.${entry.messageType}`, { defaultValue: entry.messageType })}
                      </Link>
                    ) : entry.customerId ? (
                      <Link
                        to={`/customers/${entry.customerId}`}
                        className="text-[color:var(--color-brand)] hover:underline"
                      >
                        {t(`logs.smsType.${entry.messageType}`, { defaultValue: entry.messageType })}
                      </Link>
                    ) : (
                      t(`logs.smsType.${entry.messageType}`, { defaultValue: entry.messageType })
                    )}
                  </td>
                  <td className="table-text max-w-[16rem] px-4 py-3">{entry.recipient}</td>
                  <td className="px-4 py-3 text-[color:var(--color-muted)]">{entry.segments}</td>
                  <td className="px-4 py-3">
                    <OutcomeBadge status={entry.status} kind="sms" />
                    {entry.failureReason ? (
                      <div className="table-text mt-1 max-w-[20rem] text-xs break-words text-[color:var(--color-muted)]">
                        {entry.failureReason}
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      ) : null}

      <Pagination page={page} totalPages={query.data?.totalPages} onPageChange={setPage} />
    </>
  );
}
